import { useState, useEffect, useRef } from "react";
import { Skeleton, Tooltip, Input, Switch } from "antd";
import {
  FileTextOutlined, ReloadOutlined, SaveOutlined, UploadOutlined,
  HolderOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeInvisibleOutlined,
  PlusOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { DEFAULT_SECTION_ORDER } from "./ProposalDocument";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { isSuperAdmin } from "../utils/role";
import {
  useProposalTemplate, usePatchProposalTemplate, useActiveRateCard, useDeleteCustomSection,
} from "../hooks/useRateCard";
import { uploadProposalImage } from "../services/rateCardService";
import {
  TextField, NumField, HtmlField, ListEditor, PlaceholderHint, label,
} from "./contentEditors";

/**
 * Proposal & Quotation Content — every word the documents print, editable here.
 *
 * Saving is per section, not per page: the API deep-merges the patch, so two
 * people editing different sections cannot overwrite each other. A full-config
 * PUT would.
 *
 * Both documents share one config. The quotation's own content is just
 * terms.quotation and validity_days; company, offerings and the rest are shared.
 */

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 20,
};
const cardHead = {
  padding: "14px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafbff",
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
};
const cardTitle = { fontSize: 15, fontWeight: 700, color: "#111827" };
const body = { padding: 20, display: "grid", gap: 20 };
const cols = (min = 220) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 20 });

/** Labels for the fixed, orderable sections. Custom ones name themselves. */
const SECTION_LABELS = {
  executive_summary: "Executive summary",
  why_entermark: "Why Entermark",
  offerings: "Offerings",
  common_includes: "Every plan includes",
  how_we_start: "How we start",
  founder: "Founder",
  terms: "Terms",
};

/* A custom section is referenced from section_order as `custom:<id>`, so the id
   has to stay stable for the life of the section — it is generated once and
   never recomputed from the title. */
const newSectionId = () =>
  `c-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const TABS = [
  { key: "order", label: "Section order" },
  { key: "custom", label: "Custom sections" },
  { key: "company", label: "Company & Bank" },
  { key: "cover", label: "Cover subtitle" },
  { key: "summary", label: "Executive summary" },
  { key: "why", label: "Why Entermark" },
  { key: "offerings", label: "Offerings" },
  { key: "includes", label: "Every plan includes" },
  { key: "start", label: "How we start" },
  { key: "founder", label: "Founder" },
  { key: "terms", label: "Terms" },
  { key: "footer", label: "Footer" },
];

/**
 * Founder photo — upload first, paste a URL only if you have to.
 *
 * The upload happens immediately on pick so the thumbnail can confirm the file
 * actually landed; `photo_url` is set from the response and travels with the
 * section's own Save. Clearing it puts back the initials avatar.
 */
const FounderPhoto = ({ url, name, editable, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Let the same file be chosen again after a failed attempt.
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadProposalImage(file);
      onChange(uploaded);
      toast.success("Photo uploaded — remember to save this section.");
    } catch (err) {
      toast.error(err?.message || "The image could not be uploaded");
    } finally {
      setUploading(false);
    }
  };

  const thumb = { width: 76, height: 76, borderRadius: 14, objectFit: "cover", border: "2px solid #eef2ff", flexShrink: 0 };
  const btn = {
    padding: "7px 16px", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
    border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#5b48e8",
  };

  return (
    <div>
      <label style={label}>Founder photo</label>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        {url ? (
          <img src={url} alt={name || "Founder"} style={thumb} />
        ) : (
          /* The same fallback the document renders, so the preview never lies. */
          <div style={{ ...thumb, background: "linear-gradient(135deg,#5b48e8,#7c3aed)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800 }}>
            {(name || "?").trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?"}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 220 }}>
          {editable ? (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input ref={inputRef} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} />
                <button style={btn} onClick={() => inputRef.current?.click()} disabled={uploading}>
                  <UploadOutlined /> {uploading ? "Uploading…" : url ? "Replace photo" : "Upload photo"}
                </button>
                {url && (
                  <button
                    style={{ ...btn, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626" }}
                    onClick={() => onChange(null)}
                    disabled={uploading}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, lineHeight: 1.5 }}>
                No photo means the initials avatar above.{" "}
                <button
                  onClick={() => setShowUrlField((s) => !s)}
                  style={{ border: "none", background: "none", padding: 0, color: "#5b48e8", fontWeight: 700, cursor: "pointer", fontSize: 11 }}
                >
                  {showUrlField ? "Hide URL field" : "Paste a URL instead"}
                </button>
              </div>
              {showUrlField && (
                <div style={{ marginTop: 10 }}>
                  <Input
                    value={url ?? ""}
                    onChange={(e) => onChange(e.target.value || null)}
                    placeholder="https://… (absolute URL)"
                  />
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
                    Must be absolute — the document is printed and emailed, where a
                    relative path resolves to nothing.
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: url ? "#6b7280" : "#d1d5db", wordBreak: "break-all" }}>
              {url || "No photo — the initials avatar is used."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Section order — the sequence the document prints its narrative sections in,
 * and which of them print at all.
 *
 * A section left out of the list is hidden, so this screen shows two groups: the
 * order itself, and everything currently excluded, ready to be put back.
 */
const SectionOrderEditor = ({ order, customSections, editable, onChange, onDeleteCustom }) => {
  const dragIndex = useRef(null);

  const labelFor = (key) => {
    if (String(key).startsWith("custom:")) {
      const id = String(key).slice("custom:".length);
      const custom = customSections.find((c) => String(c.id) === id);
      return { text: custom?.title || `Untitled section (#${id})`, custom: true, missing: !custom };
    }
    return { text: SECTION_LABELS[key] || key, custom: false };
  };

  /* Anything the config knows about but the order does not — the hidden set. */
  const known = [...Object.keys(SECTION_LABELS), ...customSections.map((c) => `custom:${c.id}`)];
  const hidden = known.filter((k) => !order.includes(k));

  const move = (from, to) => {
    if (from == null || to == null || from === to || to < 0 || to >= order.length) return;
    const next = [...order];
    const [key] = next.splice(from, 1);
    next.splice(to, 0, key);
    onChange(next);
  };

  const row = {
    display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
    border: "1px solid #eef2ff", borderRadius: 10, background: "#fff", marginBottom: 8,
  };
  const iconBtn = (disabled) => ({
    width: 26, height: 26, borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff",
    color: disabled ? "#d1d5db" : "#6b7280", cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11,
  });

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div>
        <label style={label}>Printed in this order</label>
        {order.length === 0 && (
          <div style={{ fontSize: 13, color: "#9ca3af" }}>
            Every section is hidden — the document will show only the cover, pricing and quotation.
          </div>
        )}
        {order.map((key, i) => {
          const info = labelFor(key);
          return (
            <div
              key={key}
              style={{ ...row, ...(info.missing ? { borderColor: "#fde68a", background: "#fffbeb" } : {}) }}
              draggable={editable}
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { move(dragIndex.current, i); dragIndex.current = null; }}
            >
              {editable && (
                <Tooltip title="Drag to reorder">
                  <span style={{ cursor: "grab", color: "#c7cbe0", fontSize: 15 }}><HolderOutlined /></span>
                </Tooltip>
              )}
              <span style={{ width: 20, fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>{i + 1}.</span>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", flex: 1 }}>
                {info.text}
                {info.custom && (
                  <span style={{ marginLeft: 8, background: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4", borderRadius: 20, padding: "1px 8px", fontSize: 10.5, fontWeight: 700 }}>
                    Custom
                  </span>
                )}
                {info.missing && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "#b45309", fontWeight: 600 }}>
                    ⚠ this section no longer exists
                  </span>
                )}
              </span>
              {editable && (
                <div style={{ display: "flex", gap: 5 }}>
                  <Tooltip title="Move up">
                    <button style={iconBtn(i === 0)} disabled={i === 0} onClick={() => move(i, i - 1)}>
                      <ArrowUpOutlined />
                    </button>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <button style={iconBtn(i === order.length - 1)} disabled={i === order.length - 1} onClick={() => move(i, i + 1)}>
                      <ArrowDownOutlined />
                    </button>
                  </Tooltip>
                  <Tooltip title="Hide from the document — keeps the section">
                    <button
                      style={{ ...iconBtn(false), border: "1px solid #fde68a", background: "#fffbeb", color: "#b45309" }}
                      onClick={() => onChange(order.filter((k) => k !== key))}
                    >
                      <EyeInvisibleOutlined />
                    </button>
                  </Tooltip>
                  {/* Only a custom section can be deleted outright — the built-in
                      ones have nothing to delete, they can only be hidden. */}
                  {info.custom && (
                    <Tooltip title="Delete this custom section">
                      <button
                        style={{ ...iconBtn(false), border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626" }}
                        onClick={() => onDeleteCustom(String(key).slice("custom:".length), info.text)}
                      >
                        <DeleteOutlined />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hidden.length > 0 && (
        <div>
          <label style={label}>Hidden — not printed</label>
          {hidden.map((key) => {
            const info = labelFor(key);
            return (
              <div key={key} style={{ ...row, background: "#fafbff", borderStyle: "dashed" }}>
                <span style={{ fontSize: 13.5, color: "#6b7280", flex: 1 }}>
                  {info.text}
                  {info.custom && (
                    <span style={{ marginLeft: 8, background: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4", borderRadius: 20, padding: "1px 8px", fontSize: 10.5, fontWeight: 700 }}>
                      Custom
                    </span>
                  )}
                </span>
                {editable && (
                  <button
                    style={{ padding: "4px 12px", borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    onClick={() => onChange([...order, key])}
                  >
                    Show
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>
        The cover, the pricing table, the plan features, the cost breakdown, the signatures and
        the quotation page are fixed — only the sections above move.
      </div>
    </div>
  );
};

const ProposalContent = () => {
  /* No segment: the editor needs every segment's raw keys, not one resolved set. */
  const { data: template, isLoading, refetch, isFetching } = useProposalTemplate();
  /* Segments live on the rate card — corporate and attorney are built in, the
     rest are custom. "default" is the copy a custom segment inherits when it has
     none of its own. */
  const { data: rateCard } = useActiveRateCard();
  const segmentKeys = [
    "corporate",
    "attorney",
    ...Object.keys(rateCard?.config?.custom_segments || {}),
    "default",
  ];
  const segmentLabel = (key) => {
    if (key === "corporate") return "Corporate";
    if (key === "attorney") return "Attorney";
    if (key === "default") return "Default (used by any segment without its own)";
    return rateCard?.config?.custom_segments?.[key]?.label || key;
  };
  const patchMutation = usePatchProposalTemplate();
  const deleteSection = useDeleteCustomSection();
  const mayEdit = isSuperAdmin();

  const [tab, setTab] = useState("company");
  const [editingTab, setEditingTab] = useState(null);   // only one section at a time
  const [draft, setDraft] = useState(null);

  /* Deep copy: editing must not touch the query cache, or Cancel would have
     nothing to fall back to. */
  useEffect(() => {
    if (template) setDraft(JSON.parse(JSON.stringify(template)));
  }, [template]);

  if (isLoading || !draft) {
    return (
      <div className="main-content"><div className="page-content"><div className="container-fluid">
        <div style={{ ...card, padding: 24 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
      </div></div></div>
    );
  }

  /* One tab per custom section, keyed `custom:<id>` — the same key the document
     order uses, so nothing has to be mapped between the two. */
  const customSections = draft.custom_sections || [];
  const allTabs = [
    ...TABS,
    ...customSections.map((c, i) => ({
      key: `custom:${c.id}`,
      label: c.title?.trim() || `Untitled section ${i + 1}`,
      custom: true,
    })),
  ];

  const editable = (key) => mayEdit && editingTab === key;

  /* Deleting a custom section touches two places at once: the section itself and
     its reference in the order. Doing one without the other leaves either a
     dangling key or an orphan nobody can reach. */
  const deleteCustomSection = (id, title) =>
    Swal.fire({
      title: `Delete "${title || "this section"}"?`,
      text: "It will be removed from the document and from the order.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      /* A section that was added but never saved exists only in this draft —
         there is nothing on the server to call about. */
      const onServer = (template?.custom_sections || []).some((c) => String(c.id) === String(id));
      if (!onServer) {
        setDraft((prev) => ({
          ...prev,
          custom_sections: (prev.custom_sections || []).filter((c) => String(c.id) !== String(id)),
          section_order: (prev.section_order?.length ? prev.section_order : DEFAULT_SECTION_ORDER)
            .filter((k) => k !== `custom:${id}`),
        }));
        if (tab === `custom:${id}`) setTab("custom");
        return;
      }
      deleteSection.mutate(id, {
        // The API returns the new arrays; the draft takes them as they are.
        onSuccess: (data) => {
          setDraft((prev) => ({
            ...prev,
            custom_sections: data.custom_sections,
            section_order: data.section_order,
          }));
          if (tab === `custom:${id}`) setTab("custom");
        },
      });
    });

  const setAt = (path, value) =>
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (node[path[i]] === undefined) node[path[i]] = {};
        node = node[path[i]];
      }
      node[path[path.length - 1]] = value;
      return next;
    });

  /** Sends only this block; everything else on the server is left alone. */
  const saveBlock = (key, patch) =>
    patchMutation.mutate(patch, { onSuccess: () => setEditingTab(null) });

  const cancel = () => {
    setDraft(JSON.parse(JSON.stringify(template)));
    setEditingTab(null);
  };

  /** Per-section Edit / Cancel + Save, so the header of each card owns its state. */
  const SectionActions = ({ tabKey, patch }) => {
    if (!mayEdit) return null;
    const on = editingTab === tabKey;
    const btn = {
      padding: "6px 16px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
      border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#5b48e8",
    };
    if (!on) {
      return (
        <button style={{ ...btn, marginLeft: "auto" }} onClick={() => setEditingTab(tabKey)}>
          Edit
        </button>
      );
    }
    return (
      <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
        <button
          style={{ ...btn, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280" }}
          onClick={cancel}
          disabled={patchMutation.isPending}
        >
          Cancel
        </button>
        <button
          style={{ ...btn, border: "none", background: "#5b48e8", color: "#fff", display: "inline-flex", alignItems: "center", gap: 6 }}
          onClick={() => saveBlock(tabKey, patch())}
          disabled={patchMutation.isPending}
        >
          <SaveOutlined /> {patchMutation.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    );
  };

  const company = draft.company || {};
  const bank = company.bank || {};

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <FileTextOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>Proposal &amp; Quotation Content</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  Every word the documents print
                </p>
              </div>
            </div>
            <Tooltip title="Refresh">
              <button
                onClick={() => refetch()}
                style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
              >
                <ReloadOutlined spin={isFetching} />
              </button>
            </Tooltip>
          </div>

          {!mayEdit && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 16px", marginBottom: 18, fontSize: 13, color: "#92400e" }}>
              You can read this content, but only a superadmin can edit it.
            </div>
          )}

          {/* Tabs — the fixed sections, then one per custom section, so a
              custom block is reachable the same way the built-in ones are. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {allTabs.map((t) => {
              const on = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); if (editingTab && editingTab !== t.key) cancel(); }}
                  style={{
                    padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
                    border: `1.5px solid ${on ? "#5b48e8" : "#e5e7eb"}`,
                    background: on ? "#5b48e8" : "#fff", color: on ? "#fff" : "#6b7280",
                  }}
                >
                  {t.label}
                  {t.custom && (
                    <span style={{ marginLeft: 7, background: on ? "rgba(255,255,255,.22)" : "#f0fdfa", color: on ? "#fff" : "#0d9488", border: `1px solid ${on ? "transparent" : "#99f6e4"}`, borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>
                      Custom
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Section order ── */}
          {tab === "order" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Section order</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>Drag, or use the arrows</span>
                <SectionActions
                  tabKey="order"
                  /* Both arrays travel together. A custom section can be deleted
                     from this tab, and sending only the order would drop the
                     reference while leaving the section in the database — it
                     came back on the next reload. */
                  patch={() => ({
                    section_order: draft.section_order || DEFAULT_SECTION_ORDER,
                    custom_sections: draft.custom_sections || [],
                  })}
                />
              </div>
              <div style={body}>
                <SectionOrderEditor
                  order={draft.section_order?.length ? draft.section_order : DEFAULT_SECTION_ORDER}
                  customSections={draft.custom_sections || []}
                  editable={editable("order")}
                  onChange={(v) => setAt(["section_order"], v)}
                  onDeleteCustom={deleteCustomSection}
                />
              </div>
            </div>
          )}

          {/* ── Custom sections ── */}
          {tab === "custom" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Custom sections</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  Extra blocks you can place anywhere in the document
                </span>
                <SectionActions
                  tabKey="custom"
                  /* The order travels with the sections: adding or deleting one
                     changes both, and sending only half would leave a dangling
                     `custom:<id>` in the order. */
                  patch={() => ({
                    custom_sections: draft.custom_sections || [],
                    section_order: draft.section_order || DEFAULT_SECTION_ORDER,
                  })}
                />
              </div>
              <div style={body}>
                {(draft.custom_sections || []).length === 0 && (
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>
                    No custom sections yet{editable("custom") ? " — add the first one below." : "."}
                  </div>
                )}

                {(draft.custom_sections || []).map((section, i) => (
                  <div
                    key={section.id}
                    style={{
                      border: "1px solid #eef2ff", borderRadius: 12, padding: 16,
                      background: section.enabled === false ? "#fafbff" : "#fff",
                      opacity: section.enabled === false ? 0.75 : 1,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>#{i + 1}</span>
                      <span style={{ fontFamily: "monospace", fontSize: 11, background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 6, padding: "2px 8px" }}>
                        custom:{section.id}
                      </span>
                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Switch
                            checked={section.enabled !== false}
                            disabled={!editable("custom")}
                            onChange={(on) =>
                              setAt(["custom_sections", i, "enabled"], on)}
                          />
                          {/* Disabled keeps the section and its place in the order
                              but stops it printing — safer than deleting it. */}
                          <span style={{ fontSize: 12, fontWeight: 600, color: section.enabled === false ? "#9ca3af" : "#16a34a" }}>
                            {section.enabled === false ? "Hidden" : "Enabled"}
                          </span>
                        </div>
                        {editable("custom") && (
                          <Tooltip title="Delete this section">
                            <button
                              onClick={() => deleteCustomSection(section.id, section.title)}
                              style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}
                            >
                              <DeleteOutlined />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 14 }}>
                      <TextField
                        title="Title" value={section.title} editable={editable("custom")}
                        onChange={(v) => setAt(["custom_sections", i, "title"], v)}
                        placeholder="e.g. Implementation timeline"
                      />
                      <TextField
                        title="Subheading (optional)" value={section.subheading} editable={editable("custom")}
                        onChange={(v) => setAt(["custom_sections", i, "subheading"], v)}
                      />
                      <HtmlField
                        title="Body" rows={5} editable={editable("custom")}
                        value={section.body}
                        onChange={(v) => setAt(["custom_sections", i, "body"], v)}
                      />
                    </div>
                  </div>
                ))}

                {editable("custom") && (
                  <button
                    onClick={() => {
                      const id = newSectionId();
                      setDraft((prev) => ({
                        ...prev,
                        custom_sections: [
                          ...(prev.custom_sections || []),
                          { id, title: "", subheading: "", body: "", enabled: true },
                        ],
                        // New sections land at the end; drag them into place on
                        // the Section order tab.
                        section_order: [
                          ...(prev.section_order?.length ? prev.section_order : DEFAULT_SECTION_ORDER),
                          `custom:${id}`,
                        ],
                      }));
                    }}
                    style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "1px dashed #c7d2fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                  >
                    <PlusOutlined /> Add section
                  </button>
                )}

                <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>
                  A new section is added at the end of the document — move it with{" "}
                  <b>Section order</b>. It prints only when enabled and present in that order.
                </div>
              </div>
            </div>
          )}

          {/* ── One custom section, opened from its own tab ── */}
          {String(tab).startsWith("custom:") && (() => {
            const id = String(tab).slice("custom:".length);
            const i = customSections.findIndex((c) => String(c.id) === id);
            const section = customSections[i];
            /* The tab can outlive its section for a render after a delete. */
            if (!section) return null;
            const on = editable(tab);
            return (
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>{section.title?.trim() || "Untitled section"}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 11, background: "#f5f3ff", color: "#5b48e8", border: "1px solid #ddd6fe", borderRadius: 6, padding: "2px 8px" }}>
                    custom:{section.id}
                  </span>
                  <SectionActions
                    tabKey={tab}
                    patch={() => ({
                      custom_sections: draft.custom_sections || [],
                      section_order: draft.section_order || DEFAULT_SECTION_ORDER,
                    })}
                  />
                </div>
                <div style={body}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <Switch
                      checked={section.enabled !== false}
                      disabled={!on}
                      onChange={(v) => setAt(["custom_sections", i, "enabled"], v)}
                    />
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: section.enabled === false ? "#9ca3af" : "#16a34a" }}>
                      {section.enabled === false ? "Hidden — not printed" : "Enabled"}
                    </span>
                    {on && (
                      <button
                        onClick={() => deleteCustomSection(id, section.title)}
                        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                      >
                        <DeleteOutlined /> Delete section
                      </button>
                    )}
                  </div>

                  <TextField
                    title="Title" value={section.title} editable={on}
                    onChange={(v) => setAt(["custom_sections", i, "title"], v)}
                    placeholder="e.g. Implementation timeline"
                  />
                  <TextField
                    title="Subheading (optional)" value={section.subheading} editable={on}
                    onChange={(v) => setAt(["custom_sections", i, "subheading"], v)}
                  />
                  <HtmlField
                    title="Body" rows={7} editable={on} value={section.body}
                    onChange={(v) => setAt(["custom_sections", i, "body"], v)}
                  />
                  <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
                    Move this section with the <b>Section order</b> tab.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Company & Bank ── */}
          {tab === "company" && (
            <>
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Company</span>
                  <SectionActions tabKey="company" patch={() => ({ company: draft.company })} />
                </div>
                <div style={body}>
                  <div style={cols()}>
                    <TextField title="Legal name" value={company.legal_name} editable={editable("company")} onChange={(v) => setAt(["company", "legal_name"], v)} />
                    <TextField title="Brand name" value={company.brand_name} editable={editable("company")} onChange={(v) => setAt(["company", "brand_name"], v)} />
                    <TextField title="CIN" value={company.cin} editable={editable("company")} onChange={(v) => setAt(["company", "cin"], v)} />
                    <TextField title="GSTIN" value={company.gstin} editable={editable("company")} onChange={(v) => setAt(["company", "gstin"], v)} />
                    <TextField title="SAC" value={company.sac} editable={editable("company")} onChange={(v) => setAt(["company", "sac"], v)} />
                    <TextField title="City" value={company.city} editable={editable("company")} onChange={(v) => setAt(["company", "city"], v)} />
                    <TextField title="Office (short)" value={company.office_short} editable={editable("company")} onChange={(v) => setAt(["company", "office_short"], v)} />
                    <TextField title="Email" value={company.email} editable={editable("company")} onChange={(v) => setAt(["company", "email"], v)} />
                    <TextField title="WhatsApp" value={company.whatsapp} editable={editable("company")} onChange={(v) => setAt(["company", "whatsapp"], v)} />
                    <TextField title="Website" value={company.website} editable={editable("company")} onChange={(v) => setAt(["company", "website"], v)} />
                    <TextField title="Signatory name" value={company.signatory_name} editable={editable("company")} onChange={(v) => setAt(["company", "signatory_name"], v)} />
                    <TextField title="Signatory role" value={company.signatory_role} editable={editable("company")} onChange={(v) => setAt(["company", "signatory_role"], v)} />
                  </div>

                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#5b48e8", marginBottom: 12 }}>
                      Bank details <span style={{ color: "#9ca3af", fontWeight: 500 }}>— printed on the quotation</span>
                    </div>
                    <div style={{ ...cols(200), background: "#fafbff", border: "1px solid #eef2ff", borderRadius: 12, padding: 16 }}>
                      <TextField title="Bank name" value={bank.name} editable={editable("company")} onChange={(v) => setAt(["company", "bank", "name"], v)} />
                      <TextField title="Account no." value={bank.account_no} editable={editable("company")} onChange={(v) => setAt(["company", "bank", "account_no"], v)} />
                      <TextField title="IFSC" value={bank.ifsc} editable={editable("company")} onChange={(v) => setAt(["company", "bank", "ifsc"], v)} />
                      <TextField title="Account name" value={bank.account_name} editable={editable("company")} onChange={(v) => setAt(["company", "bank", "account_name"], v)} />
                      <TextField title="UPI" value={bank.upi} editable={editable("company")} onChange={(v) => setAt(["company", "bank", "upi"], v)} />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Cover subtitle ── */}
          {tab === "cover" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Cover subtitle</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>The line under the cover headline</span>
                <SectionActions tabKey="cover" patch={() => ({ cover_subtitle: draft.cover_subtitle })} />
              </div>
              <div style={body}>
                {segmentKeys.map((key) => (
                  <HtmlField
                    key={key}
                    title={segmentLabel(key)} rows={3} editable={editable("cover")}
                    value={draft.cover_subtitle?.[key]}
                    onChange={(v) => setAt(["cover_subtitle", key], v)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Executive summary ── */}
          {tab === "summary" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Executive summary</span>
                <SectionActions tabKey="summary" patch={() => ({ executive_summary: draft.executive_summary })} />
              </div>
              <div style={body}>
                <div style={cols()}>
                  <TextField title="Heading" value={draft.executive_summary?.heading} editable={editable("summary")} onChange={(v) => setAt(["executive_summary", "heading"], v)} />
                  <TextField title="Subheading" value={draft.executive_summary?.subheading} editable={editable("summary")} onChange={(v) => setAt(["executive_summary", "subheading"], v)} />
                </div>
                {editable("summary") && <PlaceholderHint />}
                {segmentKeys.map((key) => (
                  <HtmlField
                    key={key}
                    title={`Body — ${segmentLabel(key)}`} rows={5} editable={editable("summary")}
                    value={draft.executive_summary?.body?.[key]}
                    onChange={(v) => setAt(["executive_summary", "body", key], v)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Why Entermark ── */}
          {tab === "why" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Why Entermark</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>Stat tiles + credibility cards</span>
                <SectionActions
                  tabKey="why"
                  patch={() => ({ why_entermark: draft.why_entermark })}
                />
              </div>
              <div style={body}>
                <div style={cols()}>
                  <TextField title="Heading" value={draft.why_entermark?.heading} editable={editable("why")} onChange={(v) => setAt(["why_entermark", "heading"], v)} />
                  <TextField title="Subheading" value={draft.why_entermark?.subheading} editable={editable("why")} onChange={(v) => setAt(["why_entermark", "subheading"], v)} />
                </div>

                <div>
                  <label style={label}>Stat tiles</label>
                  <ListEditor
                    items={draft.why_entermark?.stats || []}
                    onChange={(v) => setAt(["why_entermark", "stats"], v)}
                    editable={editable("why")}
                    addLabel="Add stat"
                    blank={{ value: "", label: "" }}
                    fields={[
                      { key: "value", title: "Value", placeholder: "75,00,000+" },
                      { key: "label", title: "Label", placeholder: "Marks monitored" },
                    ]}
                  />
                </div>

                <div>
                  <label style={label}>Credibility cards</label>
                  <ListEditor
                    items={draft.why_entermark?.cards || []}
                    onChange={(v) => setAt(["why_entermark", "cards"], v)}
                    editable={editable("why")}
                    addLabel="Add card"
                    blank={{ title: "", description: "" }}
                    fields={[
                      { key: "title", title: "Title" },
                      { key: "description", title: "Description", type: "html", rows: 2, placeholders: false },
                    ]}
                  />
                </div>

                {/* Legacy shape: the document prefers why_entermark.stats and only
                    falls back to this, so keeping both filled is duplicate work. */}
                {Array.isArray(draft.trust_stats) && draft.trust_stats.length > 0 && (
                  <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 14px", fontSize: 12.5, color: "#92400e" }}>
                    A separate <code>trust_stats</code> list also exists on the server and is only used
                    when the stat tiles above are empty. Prefer the tiles above and leave that list empty.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Offerings ── */}
          {tab === "offerings" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Top offerings</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>Shown per segment on the cover page</span>
                <SectionActions tabKey="offerings" patch={() => ({ offerings: draft.offerings })} />
              </div>
              <div style={body}>
                {segmentKeys.map((seg) => (
                  <div key={seg}>
                    <label style={label}>{segmentLabel(seg)}</label>
                    <ListEditor
                      items={draft.offerings?.[seg] || []}
                      onChange={(v) => setAt(["offerings", seg], v)}
                      editable={editable("offerings")}
                      addLabel="Add offering"
                      blank={{ icon: "", title: "", description: "" }}
                      fields={[
                        { key: "icon", title: "Icon (emoji)", placeholder: "🛡" },
                        { key: "title", title: "Title" },
                        { key: "description", title: "Description", type: "html", rows: 2, placeholders: false },
                      ]}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Common includes ── */}
          {tab === "includes" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Every plan includes</span>
                <SectionActions tabKey="includes" patch={() => ({ common_includes: draft.common_includes })} />
              </div>
              <div style={body}>
                <ListEditor
                  items={draft.common_includes || []}
                  onChange={(v) => setAt(["common_includes"], v)}
                  editable={editable("includes")}
                  addLabel="Add item"
                  blank=""
                  // A plain string list: the row itself is the value.
                  fields={[{ key: null, title: "Item", placeholder: "AI Trademark Search" }]}
                />
              </div>
            </div>
          )}

          {/* ── How we start ── */}
          {tab === "start" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>How we start</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>Numbered — the order is printed</span>
                <SectionActions tabKey="start" patch={() => ({ how_we_start: draft.how_we_start })} />
              </div>
              <div style={body}>
                <TextField title="Heading" value={draft.how_we_start?.heading} editable={editable("start")} onChange={(v) => setAt(["how_we_start", "heading"], v)} />
                <ListEditor
                  items={draft.how_we_start?.steps || []}
                  onChange={(v) => setAt(["how_we_start", "steps"], v)}
                  editable={editable("start")}
                  addLabel="Add step"
                  numbered
                  blank=""
                  fields={[{ key: null, title: "Step", type: "html", rows: 2 }]}
                />
              </div>
            </div>
          )}

          {/* ── Founder ── */}
          {tab === "founder" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Founder</span>
                <SectionActions tabKey="founder" patch={() => ({ founder: draft.founder })} />
              </div>
              <div style={body}>
                <div style={cols()}>
                  <TextField title="Heading" value={draft.founder?.heading} editable={editable("founder")} onChange={(v) => setAt(["founder", "heading"], v)} />
                  <TextField title="Subheading" value={draft.founder?.subheading} editable={editable("founder")} onChange={(v) => setAt(["founder", "subheading"], v)} />
                  <TextField title="Name" value={draft.founder?.name} editable={editable("founder")} onChange={(v) => setAt(["founder", "name"], v)} />
                  <TextField title="Title" value={draft.founder?.title} editable={editable("founder")} onChange={(v) => setAt(["founder", "title"], v)} />
                </div>
                <FounderPhoto
                  url={draft.founder?.photo_url}
                  name={draft.founder?.name}
                  editable={editable("founder")}
                  onChange={(v) => setAt(["founder", "photo_url"], v)}
                />
                <HtmlField title="Bio" rows={4} editable={editable("founder")} value={draft.founder?.bio} onChange={(v) => setAt(["founder", "bio"], v)} showPlaceholders={false} />
                <HtmlField title="Quote" rows={3} editable={editable("founder")} value={draft.founder?.quote} onChange={(v) => setAt(["founder", "quote"], v)} showPlaceholders={false} />
              </div>
            </div>
          )}

          {/* ── Terms ── */}
          {tab === "terms" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Terms &amp; Conditions</span>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  Two separate lists — the long one on the proposal, the short one on the quotation
                </span>
                <SectionActions tabKey="terms" patch={() => ({ terms: draft.terms })} />
              </div>
              <div style={body}>
                <div>
                  <label style={label}>Proposal terms</label>
                  <ListEditor
                    items={draft.terms?.proposal || []}
                    onChange={(v) => setAt(["terms", "proposal"], v)}
                    editable={editable("terms")}
                    addLabel="Add term" numbered blank=""
                    fields={[{ key: null, title: "Term", type: "html", rows: 3 }]}
                  />
                </div>
                <div>
                  <label style={label}>Quotation terms</label>
                  <ListEditor
                    items={draft.terms?.quotation || []}
                    onChange={(v) => setAt(["terms", "quotation"], v)}
                    editable={editable("terms")}
                    addLabel="Add term" numbered blank=""
                    fields={[{ key: null, title: "Term", type: "html", rows: 3 }]}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Footer ── */}
          {tab === "footer" && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Footer</span>
                <SectionActions
                  tabKey="footer"
                  patch={() => ({ footer_note: draft.footer_note, validity_days: draft.validity_days })}
                />
              </div>
              <div style={body}>
                <HtmlField
                  title="Footer note" rows={3} editable={editable("footer")}
                  value={draft.footer_note} onChange={(v) => setAt(["footer_note"], v)}
                />
                <div style={cols()}>
                  <NumField
                    title="Validity" suffix="days" value={draft.validity_days}
                    editable={editable("footer")} onChange={(v) => setAt(["validity_days"], v)}
                  />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProposalContent;
